import { IContainerList, IPackingList, IQuote } from "types/quote";
import flags from 'emoji-flags';
import { IGroupTrackingInfo, ITrackingInfo } from "types/transport";
import { toast } from "react-toastify";
import { NavigateFunction } from "react-router-dom";

/**
 * 가로(W), 세로(L), 높이(H)를 받아 용적중량을 계산하여 반환하는 함수 (TypeScript + Arrow Function)
 *
 * @param width  - 가로(cm, 소수점 첫째 자리에서 반올림)
 * @param length - 세로(cm, 소수점 첫째 자리에서 반올림)
 * @param height - 높이(cm, 소수점 첫째 자리에서 반올림)
 * @param weight - 컨테이너 용량
 * @param quantity - 컨테이너 용량
 * @returns 계산된 용적중량(kg)
 */
const calcWeight = (item: Pick<IPackingList, 'width' | 'length' | 'height' | 'weight' | 'quantity'>) => {
    const { width, length, height, weight, quantity } = item;

    // (1) 값이 모두 존재하지 않으면 빈 문자열 리턴
    if (!width || !length || !height) {
        const actualIfNoDims = calcActualWeight(Number(weight) || 0);
        return {
            volumeWeight: 0,
            actualWeight: actualIfNoDims,
            chargeableWeight: actualIfNoDims,
        };
    }

    // (2) 숫자 변환 & 소수점 첫째 자리 반올림
    const W = Math.round(Number(width));
    const L = Math.round(Number(length));
    const H = Math.round(Number(height));
    const actual = weight
        ? calcActualWeight(Number(weight))
        : 0;

    // (3) 용적중량 공식: (W * L * H) / weight
    const rawVol = (W * L * H) / 6000;

    // (4) 보정 규칙
    let volWeight: number;
    if (rawVol < 0.5) {
        volWeight = 0.5;
    }
    else if (rawVol < 1) {
        volWeight = 1;
    }
    else {
        const integer = Math.floor(rawVol);
        const dec = rawVol - integer;

        if (dec === 0) {
            volWeight = rawVol;
        } else if (dec <= 0.5) {
            volWeight = integer + 0.5;
        } else {
            volWeight = integer + 1;
        }
    }

    // (5) 청구중량은 실중량(actual)과 용적중량(volWeight) 중 큰 값
    const chargeable = weight
        ? Math.max(actual, volWeight)
        : volWeight;

    const quantityNum = quantity ? Number(quantity) : 1
    // (6) 최종적으로 문자열로 변환하여 리턴
    return {
        volumeWeight: volWeight * quantityNum,
        actualWeight: actual * quantityNum,
        chargeableWeight: chargeable * quantityNum,
    };
};

/**
 * 소수점 첫째 자리 기준 실제중량을 절상하여 반환하는 함수
 *
 * - 소수점 첫째 자리 0.5Kg 이하는 0.5Kg로 절상
 * - 소수점 첫째 자리 0.5Kg 초과는 1.0Kg로 절상
 *
 * @param weight  - 원본 중량 (kg)
 * @returns 절상된 중량 (kg)
 */
const calcActualWeight = (weight: number): number => {
    // 정수부와 소수부 분리
    const integerPart = Math.floor(weight);
    const decimalPart = weight - integerPart;

    // 소수부가 없는 경우(정수)
    if (decimalPart === 0) {
        return weight;
    }

    // 소수부가 0 < dec ≤ 0.5
    if (decimalPart <= 0.5) {
        return integerPart + 0.5;
    }

    // 소수부가 0.5 초과
    return integerPart + 1;
};

const countryCodeToEmoji = (countryCode: string): string => {
    return flags.countryCode(countryCode) ? flags.countryCode(countryCode).emoji : `\u00A0`
}

// 견적 타입 변환
const convertQuoteType = (type: string): string => {
    if (type === 'simple')
        return '간편'
    if (type === 'detail')
        return '일반'
    if (type === 'special')
        return '특가'
    if (type === 'admin')
        return '기타'
    if (type === 'realtime')
        return '실시간'

    return ''
}

// 운송상태 일시 변환
const convertDateTime = (dateTime: string): string => {
    const date = new Date(dateTime);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
}

// 바이트 변환
const bytesToMB = (bytes: number): string => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`; // 소수점 둘째 자리까지 표시
};

// 경로 다운로드
const download = (url: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = '';
    a.target = '_blank';
    a.click();
}

// 한국시간
const getToday = (): string => {
    const now = new Date(
        new Date().toLocaleString('en-US', { timeZone: 'Asia/Seoul' })
    );
    return now.toISOString().split('T')[0]; // 'YYYY-MM-DD'
};

// 텔레 시간 발송
const getKoreaDateTime = (): string => {
    const now = new Date();

    // 한국 시간으로 변환
    const koreaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));

    const pad = (n: number) => String(n).padStart(2, '0');

    const yyyyMMdd = `${koreaTime.getFullYear()}.${pad(koreaTime.getMonth() + 1)}.${pad(koreaTime.getDate())}`;
    const HHmmss = `${pad(koreaTime.getHours())}:${pad(koreaTime.getMinutes())}:${pad(koreaTime.getSeconds())}`;

    return `${yyyyMMdd} ${HHmmss}`
}


// 운송 항공 그룹화
const groupByStation = (trackingInfo: ITrackingInfo[]): IGroupTrackingInfo[] => {
    const grouped: { [station: string]: IGroupTrackingInfo } = {};

    trackingInfo.forEach(item => {
        const stationKey = item.station;

        // 처음 station이 등장하면 초기화
        if (!grouped[stationKey]) {
            grouped[stationKey] = {
                station: item.station,
                flight_number: item.flight_number,
                list: [],
            };
        }

        if (!grouped[stationKey].flight_number && item.flight_number) {
            grouped[stationKey].flight_number = item.flight_number;
        }

        grouped[item.station].list.push({
            plan_date: item.plan_date,
            actual_date: item.actual_date,
            event: item.event,
            status: item.status,
            piece: item.piece,
            weight: item.weight,
            checkpoint_status: item.checkpoint_status,
            checkpoint_substatus: item.checkpoint_substatus,
        });
    });

    return Object.values(grouped);
};

const currencySymbol: Record<string, string> = {
    USD: '$',
    KRW: '₩',
    EUR: '€',
    JPY: '¥',
    CNY: '¥',
    GBP: '£',
};

// 리스트 출발,도착지 나라 강조 
const pointCountry = (code: string): string => {
    if (code?.length !== 5) return code
    const [country, restrCode] = [code.slice(0, 2), code.slice(2)];

    return `<span class="font-bold">${country}</span> ${restrCode}`
}

const addContainerItem = (
    containerInfo: IContainerList,
    setContainerInfo: React.Dispatch<React.SetStateAction<IContainerList>>,
    setContainerFile: React.Dispatch<React.SetStateAction<File[][]>>,
    navigate: NavigateFunction,
    setQuoteInfo: React.Dispatch<React.SetStateAction<IQuote>>,
    type: 'simple' | 'normal'
) => {

    if (containerInfo.require === 'flexi') {
        if (typeof containerInfo.flexiBag !== 'boolean') return toast.error('FLEXI BAG을 선택주세요.')
    }

    if (containerInfo.require === 'frozen') {
        if (!containerInfo.tempMin) return toast.error('최소 온도를 넣어주세요.')
        if (!containerInfo.tempMax) return toast.error('최대 온도를 넣어주세요.')
        // if (typeof containerInfo.isVent !== 'boolean') return toast.error('VENT여부를 선택해주세요.')
    }

    if (containerInfo.isDangerous) {
        // 위험물 파일 체크해야함
        if (!containerInfo.dangerFile?.length) return toast.error('msds파일을 업로드 해주세요.')
    }

    const { dangerFile, ...rest } = containerInfo
    setContainerFile((prev: any) => [...prev, dangerFile ?? []]);

    setQuoteInfo((p: any) => {
        const newState = {
            ...p,
            step2: {
                ...p.step2!,
                containerList: [
                    ...(p.step2?.containerList ?? []),
                    rest,
                ],
            },
        };

        window.sessionStorage.setItem(type, JSON.stringify(newState));
        return newState;
    });

    setContainerInfo({} as IContainerList)
    navigate(`/quote/${type}/confirm`);
};

const addPackingItem = (
    packingInfo: IPackingList,
    setPackingInfo: React.Dispatch<React.SetStateAction<IPackingList>>,
    navigate: NavigateFunction,
    setQuoteInfo: React.Dispatch<React.SetStateAction<IQuote>>,
    type: 'simple' | 'normal'
) => {
    if (!validatePackingItem(packingInfo)) {
        toast.error('포장 정보 필수값을 입력해주세요.')
        return
    }
    setQuoteInfo((p: any) => {
        const newState = {
            ...p,
            step2: {
                ...p.step2!,
                packingList: [
                    ...(p.step2?.packingList ?? []),
                    packingInfo,
                ],
            },
        };

        window.sessionStorage.setItem(type, JSON.stringify(newState));
        setPackingInfo({} as IPackingList)
        return newState;
    });

    setTimeout(() => {
        navigate(`/quote/${type}/confirm`);
    }, 200)
};

const validatePackingItem = (item?: IPackingList, isGage: boolean = false) =>
    !!item &&
    typeof item.length === 'number' &&
    typeof item.width === 'number' &&
    typeof item.height === 'number' &&
    typeof item.weight === 'number' &&
    !!item.packageType &&
    typeof item.quantity === 'number' &&
    typeof item.isStackable === 'boolean' &&
    (isGage ? !!item.gage : true);

const validatePackingList = (list?: IPackingList[], isGage: boolean = false) =>
    Array.isArray(list) &&
    list.length > 0 &&
    list.every(item =>
        typeof item.length === 'number' &&
        typeof item.width === 'number' &&
        typeof item.height === 'number' &&
        typeof item.weight === 'number' &&
        !!item.packageType &&
        typeof item.quantity === 'number' &&
        typeof item.isStackable === 'boolean' &&
        (isGage ? !!item.gage : true)
    );

const validateContainerList = (list?: IContainerList[]) =>
    Array.isArray(list) && list.length > 0 &&
    list.every(item =>
        !!item.containerType &&
        !!item.containerWeight &&
        typeof item.containerQuantity === 'number'
    );

// 검색 날짜 변환
const formatDate = (date: Date | null): string => {
    if (!date) return ''
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // 월은 0부터 시작
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

// 뉴스 html 태그 제거 함수
const stripHtml = (html: string): string => {
    return html.replace(/<[^>]+>/g, ""); // 태그 제거
};

// 스크롤 위로
const goToTop = () => {
    window.scrollTo({
        top: 0,
        behavior: "smooth"
    })
}

export {
    calcWeight,
    countryCodeToEmoji,
    convertQuoteType,
    convertDateTime,
    bytesToMB,
    download,
    getToday,
    getKoreaDateTime,
    groupByStation,
    currencySymbol,
    pointCountry,
    addContainerItem,
    addPackingItem,
    validatePackingItem,
    validateContainerList,
    validatePackingList,
    formatDate,
    stripHtml,
    goToTop
}